import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Box } from '@/components/ui/box';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { cn } from '@/utils/classUtils';
import { DtUIMessage, MessageClassification, UITools } from '@ditto/shared';
import { ToolUIPart } from 'ai';
import { Circle, LucideIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { Chat } from './components/Chat';

export const AiReasoningBlock: FC<{
  message: DtUIMessage;
  isThinking: boolean;
  isLatestMessage: boolean;
}> = ({ message, isThinking, isLatestMessage }) => {
  // Group tool parts by tool name, maintaining order of first appearance
  const groupedToolMessages = useMemo(() => {
    const toolParts = message.parts.filter(part =>
      part.type.startsWith('tool-')
    ) as ToolUIPart<UITools>[];

    // Group by tool name - Map preserves insertion order (first appearance order)
    // e.g., [knowledgeSearch, fdaSearch, knowledgeSearch] -> [[knowledgeSearch, [part1, part3]], [fdaSearch, [part2]]]
    const grouped = new Map<string, ToolUIPart<UITools>[]>();
    for (const part of toolParts) {
      const toolName = part.type.slice(5); // Remove "tool-" prefix
      if (!grouped.has(toolName)) {
        grouped.set(toolName, []);
      }
      grouped.get(toolName)!.push(part);
    }

    return Array.from(grouped.entries()); // [[toolName, parts[]], ...] in first-appearance order
  }, [message]);

  const isInvalidMessage =
    message.metadata?.classification !== undefined &&
    message.metadata?.classification !== MessageClassification.VALID;

  // If region inference is required, show success (AI has completed its analysis and is waiting for user input)
  const isRegionInferenceRequired =
    message.metadata?.regionInferenceRequired === true;

  // If message is not of type valid, we show "success" as the reasoning is over
  // and we have successfully established that the question is out of scope.
  // Also show success if region inference is required (AI is waiting for user to select region)
  const status =
    isInvalidMessage || isRegionInferenceRequired
      ? 'success'
      : isThinking && isLatestMessage
        ? 'loading'
        : 'success';

  return (
    <div>
      <FlexCol className="rounded-lg bg-white border border-neutral-200 h-fit [&>div:last-of-type]:pb-3 [&>div:first-of-type]:pt-3 [&>div:last-of-type>#vertical-dash]:hidden">
        <AIReasoningItem
          messages={{
            success: 'AI reasoning completed for your query.',
            error: 'Error analyzing your question.',
            loading: 'Analyzing your question...',
          }}
          status={status}
          isOpenable={false}
          isLast={false}
        />

        {groupedToolMessages.length > 0 &&
          groupedToolMessages.map(([toolName, toolParts], idx) => (
            <Chat.AiToolMessage
              key={`${message.id}-tool-${toolName}`}
              toolName={toolName}
              toolParts={toolParts}
              isLast={idx === groupedToolMessages.length - 1}
            />
          ))}
      </FlexCol>
    </div>
  );
};

export const AIReasoningItem: FC<{
  isLast: boolean;
  messages: { success: string; error: string; loading: string };
  icon?: LucideIcon;
  status: 'success' | 'error' | 'loading';
  isOpenable?: boolean;
  children?: React.ReactNode;
}> = ({ messages, icon: Icon, status, isOpenable, children, isLast }) => {
  const displayMessage = messages[status as keyof typeof messages];
  const [accordionValue, setAccordionValue] = useState<string>('');

  const isOpen = accordionValue === 'item-1';

  if (isOpenable && status === 'success') {
    return (
      <Accordion
        type="single"
        collapsible
        value={accordionValue}
        onValueChange={setAccordionValue}
        className={cn(
          'rounded-md px-2 transition-colors',
          isOpen ? 'bg-neutral-50' : 'bg-white'
        )}
      >
        <AccordionItem value="item-1">
          <AccordionTrigger className="mr-2 ml-2 text-neutral-600 h-[56px] border-none">
            <FlexRow
              alignItems="center"
              className="justify-between h-full border-none"
              gap="3"
            >
              {Icon ? <Icon className="w-5 h-15" /> : <Circle size={8} />}
              <Text variant="body" color="tertiary">
                {displayMessage}
              </Text>
            </FlexRow>
          </AccordionTrigger>
          <div
            id="vertical-dash"
            className={cn(
              'w-0.5 h-2 py-2 mx-[18px] bg-neutral-200 ',
              (isOpen || isLast) && 'hidden'
            )}
          />
          <AccordionContent className="h-full w-full px-2">
            <div className="flex w-full items-stretch gap-2">
              <div className="w-0.5 bg-neutral-200 mx-2.5" />
              {children}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <FlexCol alignItems="start">
      <Box className={'px-4 py-1 bg-white rounded-lg flex items-center h-fit'}>
        <FlexRow alignItems="center" gap="3">
          <FlexRow className="w-5 h-5 justify-center items-center">
            {Icon ? (
              <Icon
                className={cn(
                  'text-neutral-600 w-5 h-5',
                  status === 'loading' && 'animate-pulse  text-yellow-600',
                  status === 'error' && 'text-red-500'
                )}
              />
            ) : (
              <Circle
                className={cn(
                  'text-neutral-600 fill-neutral-600 w-2 h-2',
                  status === 'loading' &&
                    'animate-pulse fill-yellow-600 text-yellow-600',
                  status === 'error' && 'fill-red-500 text-red-500'
                )}
              />
            )}
          </FlexRow>
          <Text variant="body" color="tertiary" className="font-medium">
            {displayMessage}
          </Text>
        </FlexRow>
      </Box>
      <div
        id="vertical-dash"
        className={cn(
          'w-0.5 h-3 py-2 mx-[25px] self-stretch bg-neutral-200 ',
          isLast && 'hidden'
        )}
      />
    </FlexCol>
  );
};
